import type { ProviderType } from "@wags/catalog/pure.ts";
import {
    type ContactListingState,
    type ContactMessageIntent,
    DEFAULT_CONTACT_MESSAGE_INTENT,
} from "@wags/profile-contracts/contact-session.contract.ts";
import type { JsonValue } from "@wags/utils/json.ts";
import type { PostHogInterface } from "posthog-js";
import { useRef } from "react";

export const CONTACT_FUNNEL_EVENTS = {
    attempted: "contact_attempted",
    formShown: "contact_form_shown",
    compatibilityAnswered: "contact_compat_answered",
    mismatchDismissed: "contact_mismatch_dismissed",
    copyMessageResult: "contact_copy_message_result",
    openMessagesResult: "contact_open_messages_result",
    handoffStuck: "contact_handoff_stuck",
    handoffObservation: "contact_handoff_observation",
    handoffQrDisplayed: "contact_handoff_qr_displayed",
    sessionCreateFailed: "contact_session_create_failed",
} as const;

export type ContactAuthState = boolean | "loading";

export type ContactFunnelProperties = {
    dogId: string;
    providerType: ProviderType | "unknown";
    source: string;
    contact_auth_state: ContactAuthState;
    intent: ContactMessageIntent;
    listing_state?: ContactListingState;
};

export function contactFunnelProperties({
    listingId,
    providerType,
    source,
    isLoaded,
    isSignedIn,
    intent = DEFAULT_CONTACT_MESSAGE_INTENT,
    listingState,
}: {
    listingId: string;
    providerType: ProviderType | undefined;
    source: string;
    isLoaded: boolean;
    isSignedIn: boolean | undefined;
    intent?: ContactMessageIntent;
    listingState?: ContactListingState;
}): ContactFunnelProperties {
    return {
        dogId: listingId,
        providerType: providerType ?? "unknown",
        source,
        contact_auth_state: isLoaded ? Boolean(isSignedIn) : "loading",
        intent,
        listing_state: listingState,
    };
}

export type ContactFormStep =
    | "compatibility_question"
    | "mismatch_warning"
    | "mismatch_overridden"
    | "strong_fit"
    | "signed_out_prompt"
    | "signed_in_form";

export function captureContactFunnelEvent(
    posthog: PostHogInterface,
    event: string,
    shared: ContactFunnelProperties,
    properties?: Record<string, JsonValue>,
) {
    try {
        posthog.capture(event, { ...shared, ...properties });
    } catch {}
}

export function useContactFormShownRef(
    posthog: PostHogInterface,
    shared: ContactFunnelProperties,
    step: ContactFormStep | null,
) {
    const shownSteps = useRef(new Set<ContactFormStep>());
    return (node: HTMLElement | null) => {
        if (!node || !step || shownSteps.current.has(step)) return;
        shownSteps.current.add(step);
        captureContactFunnelEvent(
            posthog,
            CONTACT_FUNNEL_EVENTS.formShown,
            shared,
            { step },
        );
    };
}
